import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";

const communityCategories = [
  {
    id: 1,
    categoryId: "relationships",
    title: "연애 & 인간관계",
    titleEn: "Relationships",
    description: "썸, 재혼, 외로움까지 마음을 나누는 공간",
    image: "https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?w=600&q=80&fit=crop",
    posts: 128,
  },
  {
    id: 2,
    categoryId: "travel",
    title: "여행",
    titleEn: "Travel",
    description: "국내, 해외, 여행메이트와 떠나는 이야기",
    image: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&q=80&fit=crop",
    posts: 156,
  },
  {
    id: 3,
    categoryId: "health",
    title: "건강",
    titleEn: "Wellness",
    description: "운동, 다이어트, 갱년기 — 단단한 일상",
    image: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=600&q=80&fit=crop",
    posts: 118,
  },
  {
    id: 4,
    categoryId: "hobby",
    title: "취미",
    titleEn: "Hobbies",
    description: "골프, 등산, 음악, 와인 — 결을 나누는 즐거움",
    image: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=600&q=80&fit=crop",
    posts: 203,
  },
  {
    id: 5,
    categoryId: "retirement",
    title: "노후 & 인생2막",
    titleEn: "Next Chapter",
    description: "은퇴, 재테크, 해외살이 — 두 번째 인생 설계",
    image: "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=600&q=80&fit=crop",
    posts: 156,
  },
  {
    id: 6,
    categoryId: "free",
    title: "자유게시판",
    titleEn: "Free Talk",
    description: "오늘의 일상, 감성글, 일기 — 결로 잇는 글",
    image: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=600&q=80&fit=crop",
    posts: 231,
  },
  {
    id: 7,
    categoryId: "today_moment",
    title: "오늘의 하루",
    titleEn: "Today",
    description: "지금 이 순간을 짧은 글과 사진으로",
    image: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=600&q=80&fit=crop",
    posts: 142,
  },
  {
    id: 8,
    categoryId: "anonymous_advice",
    title: "익명 고민상담",
    titleEn: "Advice",
    description: "아무도 모르게, 조용히 털어놓는 마음",
    image: "https://images.unsplash.com/photo-1471107340929-a87cd0f5b5f3?w=600&q=80&fit=crop",
    posts: 95,
  },
];

export const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const navigate = useNavigate();
  const itemsPerPage = 4;
  const totalPages = Math.ceil(communityCategories.length / itemsPerPage);

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalPages);
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused, totalPages]);

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % totalPages);
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);

  const visibleItems = communityCategories.slice(
    currentIndex * itemsPerPage,
    currentIndex * itemsPerPage + itemsPerPage
  );

  const handleCardClick = (categoryId) => {
    navigate(`/community?cat=${categoryId}`);
  };

  return (
    <section
      id="community-board"
      className="py-12 lg:py-16 bg-[#faf8f5] relative overflow-hidden"
      data-testid="community-board-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        {/* Section Header */}
        <div className="mb-10">
          {/* Editorial eyebrow — Premium Social Community */}
          <div className="flex items-center gap-4 mb-6" data-testid="community-eyebrow">
            <span className="text-[11px] sm:text-xs font-semibold tracking-[0.32em] uppercase text-[#c4956a]">
              Premium · Social · Community
            </span>
            <span className="hidden sm:inline-block flex-1 h-px bg-gradient-to-r from-[#c4956a]/40 via-[#c4956a]/15 to-transparent max-w-[180px]" />
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div className="relative">
              <p className="text-2xl sm:text-3xl lg:text-4xl font-light text-stone-800 leading-tight tracking-tight mb-1">
                따뜻한 이야기들이 머무는,
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
                <span className="text-black">중년들만의 공감 놀이터</span>
              </h2>
            </div>
            <p className="text-stone-400 text-xl md:text-2xl max-w-md leading-relaxed">
              공감, 관계, 기록 그리고 성장
              <br />중년의 삶과 감정을 나누는 감성 커뮤니티
            </p>
          </div>
        </div>

        {/* 4 Emotional Philosophy — text-only, no buttons */}
        <div className="mb-12 lg:mb-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-10 lg:gap-x-12" data-testid="philosophy-text-block">
          {[
            {
              t: "내 삶의 흔적을 기록하는 공간",
              d: "하루의 감정과 지나온 시간들이 따뜻한 이야기로 차곡차곡 쌓여갑니다.",
            },
            {
              t: "공감과 이해가 머무는 공간",
              d: "'나도 그래요' 그 한마디가 외로웠던 마음에 작은 온기를 전합니다.",
            },
            {
              t: "함께 배우고 성장하는 공간",
              d: "사랑을 배우고 사람을 이해하며 더 좋은 관계와 더 나은 나로 성장해갑니다.",
            },
            {
              t: "글로벌 한인 중년들의 연결",
              d: "세계 곳곳의 한인 중년들이 언어와 문화, 삶의 공감으로 자연스럽게 이어집니다.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="relative group cursor-default transition-all duration-500 ease-out hover:-translate-y-2 hover:scale-[1.12] origin-top motion-reduce:transform-none"
              data-testid={`philosophy-card-${idx}`}
            >
              <div className="w-8 h-px bg-[#c4956a]/60 mb-5 transition-all duration-500 group-hover:w-14 group-hover:bg-[#c4956a]" />
              <h3 className="text-stone-900 text-[17px] lg:text-[18px] font-semibold leading-snug tracking-tight mb-3 transition-colors duration-500 group-hover:text-burgundy-700">
                {item.t}
              </h3>
              <p className="text-stone-500 text-[13.5px] lg:text-[14px] leading-[1.85] transition-colors duration-500 group-hover:text-stone-700">
                {item.d}
              </p>
            </div>
          ))}
        </div>

        {/* Cards Carousel */}
        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute -left-3 lg:-left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white/90 backdrop-blur-sm rounded-full shadow-md flex items-center justify-center hover:bg-white hover:shadow-lg transition-all duration-300 group"
            data-testid="community-prev-btn"
          >
            <ChevronLeft className="w-5 h-5 text-stone-400 group-hover:text-stone-700 transition-colors" />
          </button>

          {/* Cards Grid */}
          <div key={currentIndex} className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {visibleItems.map((category, index) => (
              <div
                key={`${currentIndex}-${category.id}`}
                onClick={() => handleCardClick(category.categoryId)}
                className="group relative rounded-2xl overflow-hidden cursor-pointer shadow-sm hover:shadow-xl transition-shadow duration-500"
                style={{
                  aspectRatio: "3/4",
                  animation: `cardFadeIn 0.5s ease-out ${index * 100}ms both`,
                }}
                data-testid={`community-card-${index}`}
              >
                {/* Background Image */}
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-105"
                  style={{ backgroundImage: `url(${category.image})` }}
                />

                {/* Bright Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent group-hover:from-black/65 transition-all duration-500" />

                {/* Soft warm tint */}
                <div className="absolute inset-0 bg-gradient-to-br from-rose-100/10 via-transparent to-amber-50/10" />

                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-between p-5 md:p-6">
                  {/* Top: English Label + Arrow */}
                  <div className="flex items-start justify-between">
                    <span className="text-sm md:text-base font-bold tracking-[0.12em] uppercase text-white drop-shadow-md" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.35)' }}>
                      {category.titleEn}
                    </span>
                    <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                      <ArrowUpRight className="w-4 h-4 text-white" />
                    </div>
                  </div>

                  {/* Bottom: Korean Title + Description + Post Count */}
                  <div className="translate-y-1 group-hover:translate-y-0 transition-transform duration-400">
                    <h3 className="text-white font-bold text-lg md:text-xl leading-snug mb-1.5 drop-shadow-md">
                      {category.title}
                    </h3>
                    <p className="text-white/75 text-xs md:text-sm leading-relaxed mb-3 line-clamp-2 drop-shadow-sm">
                      {category.description}
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="h-[1px] w-5 bg-white/40" />
                      <span className="text-[10px] font-medium text-white/60 tracking-wider">
                        {category.posts} posts
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          <button
            onClick={nextSlide}
            className="absolute -right-3 lg:-right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white/90 backdrop-blur-sm rounded-full shadow-md flex items-center justify-center hover:bg-white hover:shadow-lg transition-all duration-300 group"
            data-testid="community-next-btn"
          >
            <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-stone-700 transition-colors" />
          </button>
        </div>

        {/* Page Indicators */}
        <div className="flex items-center justify-center gap-3 mt-10">
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-1 rounded-full transition-all duration-500 ${
                currentIndex === index
                  ? "w-10 bg-[#c4956a]"
                  : "w-4 bg-stone-300 hover:bg-stone-400"
              }`}
              data-testid={`community-page-${index}`}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes cardFadeIn {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};
